import {
    Box,
    Text,
    Flex,
    IconButton,
    Tooltip,
    VStack,
    useBreakpointValue
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FiEye, FiEdit2, FiArrowDown, FiArrowUp } from 'react-icons/fi';

const EventsCardList = ({ events, sort, sortDir, onSort, loading }) => {
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const getStatus = (event) => {
        const now = new Date();
        const end = new Date(`${event.date}T${event.endTime}`);
        return end > now ? 'Nadchodzące' : 'Zakończone';
    };

    const getStatusColor = (event) => {
        const now = new Date();
        const end = new Date(`${event.date}T${event.endTime}`);
        return end > now ? 'orange' : 'green';
    };

    const columns = [
        { key: 'id', label: 'ID' },
        { key: 'title', label: 'Nazwa wydarzenia' },
        { key: 'status', label: 'Status' },
        { key: 'date', label: 'Data wydarzenia' },
        { key: 'participants', label: 'Uczestnicy' },
        { key: 'created', label: 'Data utworzenia' },
        { key: 'actions', label: 'Akcje' },
    ];

    const renderSortIcon = (key) => {
        if (sort !== key) return null;
        return sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
    };

    return (
        <Box w="100%" maxW="7xl" mx="auto">
            {!isMobile && (
                <Box
                    display="grid"
                    gridTemplateColumns="80px 1fr 130px 160px 140px 160px 100px"
                    gap="16px"
                    mb="8px"
                >
                    {columns.map(col => (
                        <Box
                            key={col.key}
                            bg="#0A4725"
                            borderRadius="0px"
                            p="12px"
                            display="flex"
                            alignItems="center"
                            fontSize="14px"
                            fontWeight="400"
                            color="white"
                            cursor={col.key !== 'actions' ? 'pointer' : 'default'}
                            onClick={() => col.key !== 'actions' && onSort(col.key)}
                            boxShadow="sm"
                        >
                            <Flex align="center" gap={1}>
                                {col.label}
                                {renderSortIcon(col.key)}
                            </Flex>
                        </Box>
                    ))}
                </Box>
            )}

            {!loading && events.length === 0 ? (
                <VStack align="center" gap={'48px'} p={'48px'}>
                    <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={'20px'} color="#363636">
                        Brak wydarzeń spełniających kryteria wyszukiwania.
                    </Text>
                </VStack>
                ) : (
                events.map(event => (
                    <Box
                        key={event.id}
                        display="grid"
                        gridTemplateColumns={{
                            base: '1fr',
                            md: '80px 1fr 130px 160px 140px 160px 100px'
                        }}
                        gap="16px"
                        bg="transparent"
                        p="0"
                        mb="12px"
                        fontFamily="Inter"
                        fontSize="14px"
                        fontWeight="400"
                    >
                        <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center" onClick={() => navigate(`/events/${event.id}/edit`)} cursor={"pointer"}>
                            <Text>{event.eventId}</Text>
                        </Box>

                        <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center" onClick={() => navigate(`/events/${event.id}/edit`)} cursor={"pointer"}>
                            <Text>{event.title}</Text>
                        </Box>

                        <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                            <Text
                                bg={getStatusColor(event)}
                                borderRadius="4px"
                                px={2}
                                py={1}
                                color="white"
                                fontSize="13px"
                            >
                                {getStatus(event)}
                            </Text>
                        </Box>

                        <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                            <Text>{moment(event.date + 'T' + event.startTime).format('D.MM.YYYY H:mm')}</Text>
                        </Box>

                        <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                            <Flex gap="16px" p="16px">
                                {event.participantsCount}/{event.maxParticipants}
                                {event.participantsCount >= event.maxParticipants && (
                                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M16 8.5C16 10.6217 15.1571 12.6566 13.6569 14.1569C12.1566 15.6571 10.1217 16.5 8 16.5C5.87827 16.5 3.84344 15.6571 2.34315 14.1569C0.842855 12.6566 0 10.6217 0 8.5C0 6.37827 0.842855 4.34344 2.34315 2.84315C3.84344 1.34285 5.87827 0.5 8 0.5C10.1217 0.5 12.1566 1.34285 13.6569 2.84315C15.1571 4.34344 16 6.37827 16 8.5ZM9 12.5C9 12.7652 8.89464 13.0196 8.70711 13.2071C8.51957 13.3946 8.26522 13.5 8 13.5C7.73478 13.5 7.48043 13.3946 7.29289 13.2071C7.10536 13.0196 7 12.7652 7 12.5C7 12.2348 7.10536 11.9804 7.29289 11.7929C7.48043 11.6054 7.73478 11.5 8 11.5C8.26522 11.5 8.51957 11.6054 8.70711 11.7929C8.89464 11.9804 9 12.2348 9 12.5ZM8 3.5C7.73478 3.5 7.48043 3.60536 7.29289 3.79289C7.10536 3.98043 7 4.23478 7 4.5V8.5C7 8.76522 7.10536 9.01957 7.29289 9.20711C7.48043 9.39464 7.73478 9.5 8 9.5C8.26522 9.5 8.51957 9.39464 8.70711 9.20711C8.89464 9.01957 9 8.76522 9 8.5V4.5C9 4.23478 8.89464 3.98043 8.70711 3.79289C8.51957 3.60536 8.26522 3.5 8 3.5Z" fill="#DC0000"/>
                                    </svg>
                                )}
                            </Flex>
                        </Box>

                        <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                            <Text>{moment(event.createdAt).format('D.MM.YYYY H:mm')}</Text>
                        </Box>

                        <Box bg="white" borderRadius="0px" p="8px" display="flex" justifyContent="center" gap={2} alignItems="center">
                            <Tooltip label="Podgląd">
                                <IconButton
                                    icon={<FiEye />}
                                    aria-label="Podgląd"
                                    size="sm"
                                    onClick={() => window.open(`/events/${event.id}/preview`, '_blank')}
                                    bg="white"
                                />
                            </Tooltip>
                            <Tooltip label="Edytuj">
                                <IconButton
                                    icon={<FiEdit2 />}
                                    aria-label="Edytuj"
                                    size="sm"
                                    onClick={() => navigate(`/events/${event.id}/edit`)}
                                    bg="white"
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
};

export default EventsCardList;
